import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { 
  Building2, Mail, UserX, Crown, Send, X, Users, Plus
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

const Organizations: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [orgMembers, setOrgMembers] = useState<any[]>([]);
  const [pendingInvites, setPendingInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showOrgDetailsDialog, setShowOrgDetailsDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  
  // Form states
  const [newOrg, setNewOrg] = useState({ name: '', description: '', admin_email: '' });
  const [inviteData, setInviteData] = useState({ email: '', role: 'org_member' });

  useEffect(() => {
    loadOrganizations();
  }, [user]);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const orgs = await apiClient.getMyOrganizations();
      setOrganizations(orgs as any[] || []);
    } catch (error: any) {
      console.error('Failed to load organizations', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load organizations"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationDetails = async (orgId: string) => {
    try {
      const details = await apiClient.getOrganizationDetails(orgId);
      setSelectedOrg(details);
      
      // Extract members from details response
      const members = (details as any)?.members || [];
      setOrgMembers(members);
      
      // Load invitations
      const invites = await apiClient.getOrganizationInvitations(orgId);
      setPendingInvites(invites as any[] || []);
      
      setShowOrgDetailsDialog(true);
    } catch (error: any) {
      console.error('Failed to load organization details', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load organization details"
      });
    }
  };

  const handleCreateOrganization = async () => {
    try {
      if (!newOrg.name || !newOrg.admin_email) {
      toast({
        title: "Validation Error",
        description: "Organization name and admin email are required"
      });
        return;
      }

      await apiClient.createOrganization(
        newOrg.name,
        newOrg.description,
        newOrg.admin_email
      );

      toast({
        title: "Success",
        description: "Organization created successfully"
      });

      setShowCreateOrgDialog(false);
      setNewOrg({ name: '', description: '', admin_email: '' });
      await loadOrganizations();
      await refreshUser();
    } catch (error: any) {
      console.error('Failed to create organization', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create organization"
      });
    }
  };

  const handleInviteMember = async () => {
    if (!selectedOrg || !inviteData.email) {
      toast({
        title: "Validation Error",
        description: "Email is required"
      });
      return;
    }

    try {
      await apiClient.inviteTeamMember(selectedOrg.id, inviteData.email, inviteData.role);
      toast({
        title: "Success",
        description: "Invitation sent successfully"
      });

      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'org_member' });
      await loadOrganizationDetails(selectedOrg.id);
    } catch (error: any) {
      console.error('Failed to invite member', error);
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation"
      });
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!selectedOrg || !confirm('Are you sure you want to remove this member?')) return;

    try {
      await apiClient.removeTeamMember(selectedOrg.id, userId);
      toast({
        title: "Success",
        description: "Member removed successfully"
      });
      await loadOrganizationDetails(selectedOrg.id);
    } catch (error: any) {
      console.error('Failed to remove member', error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove member"
      });
    }
  };

  const handleUpdateMemberRole = async (userId: string, newRole: string) => {
    if (!selectedOrg) return;

    try {
      await apiClient.updateTeamMemberRole(selectedOrg.id, userId, newRole);
      toast({
        title: "Success",
        description: "Member role updated successfully"
      });
      await loadOrganizationDetails(selectedOrg.id);
    } catch (error: any) {
      console.error('Failed to update member role', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update member role"
      });
    }
  };

  const handleCancelInvitation = async (inviteId: string) => {
    if (!selectedOrg || !confirm('Cancel this invitation?')) return;

    try {
      await apiClient.cancelInvitation(selectedOrg.id, inviteId);
      toast({
        title: "Success",
        description: "Invitation cancelled"
      });
      await loadOrganizationDetails(selectedOrg.id);
    } catch (error: any) {
      console.error('Failed to cancel invitation', error);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel invitation"
      });
    }
  };

  const handleTransferOwnership = async (newAdminId: string) => {
    if (!selectedOrg || !confirm('Transfer organization ownership? This action cannot be undone.')) return;

    try {
      await apiClient.transferOwnership(selectedOrg.id, newAdminId);
      toast({
        title: "Success",
        description: "Ownership transferred successfully"
      });
      setShowTransferDialog(false);
      await loadOrganizationDetails(selectedOrg.id);
      await refreshUser();
    } catch (error: any) {
      console.error('Failed to transfer ownership', error);
      toast({
        title: "Error",
        description: error.message || "Failed to transfer ownership"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Building2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-gray-600 dark:text-gray-300">Loading organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Organizations & Teams
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Create and manage your organizations and team members
          </p>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {organizations.length} {organizations.length === 1 ? 'organization' : 'organizations'}
            </p>
          </div>
          <Button onClick={() => setShowCreateOrgDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Create Organization
          </Button>
        </div>

        {/* Organizations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {organizations.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="w-16 h-16 text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400 mb-2">No organizations found</p>
                <p className="text-sm text-gray-400 mb-4">Create your first organization to get started</p>
                <Button onClick={() => setShowCreateOrgDialog(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Organization
                </Button>
              </CardContent>
            </Card>
          ) : (
            organizations.map((org: any) => (
              <Card key={org.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => loadOrganizationDetails(org.id)}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{org.name}</CardTitle>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {org.member_count || 0} {org.member_count === 1 ? 'member' : 'members'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {org.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                        {org.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Request Limit</span>
                      <Badge variant="secondary">
                        {org.request_limit === -1 ? 'Unlimited' : org.request_limit}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Usage</span>
                      <span className="font-medium">{org.requests_used || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Organization Dialog */}
        <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Create New Organization
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Organization Name *</Label>
                <Input
                  placeholder="Acme Corporation"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  placeholder="Brief description of the organization"
                  value={newOrg.description}
                  onChange={(e) => setNewOrg({...newOrg, description: e.target.value})}
                />
              </div>
              <div>
                <Label>Admin Email *</Label>
                <Input
                  type="email"
                  placeholder={user?.email || "admin@company.com"}
                  value={newOrg.admin_email}
                  onChange={(e) => setNewOrg({...newOrg, admin_email: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">The email address that will be the organization admin</p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={handleCreateOrganization} className="flex-1">
                  Create Organization
                </Button>
                <Button variant="outline" onClick={() => setShowCreateOrgDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Organization Details Dialog */}
        <Dialog open={showOrgDetailsDialog} onOpenChange={setShowOrgDetailsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                {selectedOrg?.name || 'Organization Details'}
              </DialogTitle>
            </DialogHeader>
            {selectedOrg && (
              <div className="space-y-6 mt-4">
                {/* Organization Info */}
                <Card>
                  <CardHeader>
                    <CardTitle>Organization Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Request Limit</Label>
                        <p className="font-semibold">{selectedOrg.request_limit === -1 ? 'Unlimited' : selectedOrg.request_limit}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Usage</Label>
                        <p className="font-semibold">{selectedOrg.requests_used || 0}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Members</Label>
                        <p className="font-semibold">{selectedOrg.member_count || 0}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Admin</Label>
                        <p className="font-semibold">{selectedOrg.admin_email || 'N/A'}</p>
                      </div>
                    </div>
                    {selectedOrg.description && (
                      <div>
                        <Label className="text-xs text-gray-500">Description</Label>
                        <p className="text-sm mt-1">{selectedOrg.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Team Members */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Team Members ({orgMembers.length})</CardTitle>
                    <Button size="sm" onClick={() => setShowInviteDialog(true)} className="gap-2">
                      <Send className="w-4 h-4" />
                      Invite Member
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {orgMembers.map((member: any) => (
                        <div key={member.id || member.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Users className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{member.email || member.user_email}</p>
                              <p className="text-xs text-gray-500">{member.first_name} {member.last_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={member.role || 'org_member'}
                              onValueChange={(v) => handleUpdateMemberRole(member.id || member.user_id, v)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="org_admin">
                                  <div className="flex items-center gap-2">
                                    <Crown className="w-3 h-3" />
                                    Admin
                                  </div>
                                </SelectItem>
                                <SelectItem value="org_member">Member</SelectItem>
                              </SelectContent>
                            </Select>
                            {member.role === 'org_admin' && (
                              <Badge variant="default" className="gap-1">
                                <Crown className="w-3 h-3" />
                                Owner
                              </Badge>
                            )}
                            {selectedOrg?.admin_id !== (member.id || member.user_id) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveMember(member.id || member.user_id)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {orgMembers.length === 0 && (
                        <p className="text-center text-gray-500 py-4">No members yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Pending Invitations */}
                {pendingInvites.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Pending Invitations ({pendingInvites.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {pendingInvites.map((invite: any) => (
                          <div key={invite.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="font-medium">{invite.email}</p>
                                <p className="text-xs text-gray-500">
                                  Invited as {invite.role === 'org_admin' ? 'Admin' : 'Member'}
                                </p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelInvitation(invite.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invite Member Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  placeholder="member@company.com"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={inviteData.role} onValueChange={(v) => setInviteData({...inviteData, role: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org_admin">Organization Admin</SelectItem>
                    <SelectItem value="org_member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={handleInviteMember} className="flex-1">Send Invitation</Button>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Transfer Ownership Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transfer Ownership</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-gray-600">
                Select a member to transfer ownership to. This action cannot be undone.
              </p>
              <div>
                <Label>New Owner</Label>
                <Select onValueChange={handleTransferOwnership}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a member" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgMembers
                      .filter((m: any) => m.role !== 'org_admin' && selectedOrg?.admin_id !== (m.id || m.user_id))
                      .map((member: any) => (
                        <SelectItem key={member.id || member.user_id} value={member.id || member.user_id}>
                          {member.email || member.user_email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowTransferDialog(false)} className="flex-1">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Organizations;

